import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Sparkles, User, LogOut, Menu, Heart } from "lucide-react";
import { useState } from "react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const links = [
  { to: "/", label: "Home" },
  { to: "/collection", label: "Collection" },
  { to: "/try-on", label: "Virtual Try-On" },
  { to: "/designer", label: "AI Designer" },
];

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 glass" data-testid="navbar">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 h-[68px] flex items-center justify-between">
        <div className="flex items-center gap-10">
          <Link to="/" className="flex flex-col leading-none" data-testid="logo-link">
            <span className="font-serif text-2xl font-bold gold-text tracking-wide">Tashora</span>
            <span className="text-[9px] font-mono uppercase tracking-[0.35em] text-gold-muted/70">Heritage Atelier</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm text-gold-muted/80 hover:text-gold transition-colors duration-200"
              >
                {l.label}
              </Link>
            ))}
            {user?.is_admin && (
              <Link to="/admin" data-testid="nav-admin" className="text-sm text-gold hover:text-gold-light transition-colors duration-200 font-semibold">
                Admin
              </Link>
            )}
          </nav>
        </div>

        <div className="flex items-center gap-4">
          <Link
            to="/wishlist"
            data-testid="wishlist-nav-button"
            className="relative p-2 text-gold-muted hover:text-gold transition-colors"
          >
            <Heart className="w-5 h-5" />
            {wishCount > 0 && (
              <span data-testid="wishlist-count" className="absolute -top-1 -right-1 bg-gold text-obsidian text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full">
                {wishCount}
              </span>
            )}
          </Link>
          <button
            onClick={() => setOpen(true)}
            data-testid="cart-button"
            className="relative p-2 text-gold-muted hover:text-gold transition-colors"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-1 -right-1 bg-gold text-obsidian text-[10px] font-bold w-4.5 h-4.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full"
              >
                {count}
              </span>
            )}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="account-menu-trigger" className="flex items-center gap-2">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full gold-border object-cover" />
                  ) : (
                    <span className="w-8 h-8 rounded-full grid place-items-center bg-secondary gold-border">
                      <User className="w-4 h-4 text-gold" />
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="glass border-gold/20">
                <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-orders">
                  <User className="w-4 h-4 mr-2" /> My Account & Orders
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/try-on")} data-testid="menu-looks">
                  <Sparkles className="w-4 h-4 mr-2" /> My Try-On Looks
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={login}
              data-testid="login-button"
              className="hidden sm:inline-flex items-center gap-2 text-sm text-obsidian bg-gold hover:bg-gold-light transition-colors px-4 py-2 rounded-full font-semibold"
            >
              Sign in
            </button>
          )}

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="md:hidden p-2 text-gold-muted" data-testid="mobile-menu-trigger">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-obsidian-2 border-gold/20">
              <div className="flex flex-col gap-6 mt-10">
                {links.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    className="font-serif text-2xl text-gold-muted"
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                ))}
                {!user && (
                  <button onClick={login} className="mt-4 bg-gold text-obsidian py-2 rounded-full font-semibold" data-testid="mobile-login-button">
                    Sign in
                  </button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
